import { useState, useEffect } from "react";

/* ─────────────────────────────────────────────
   QUESTIONS
───────────────────────────────────────────── */
const sections = [
  {
    id: "functional",
    label: "Functional Health",
    tag: "PART 01",
    accent: "#C8F562",
    intro: "How your body is actually functioning beneath the surface.",
    questions: [
      {
        id: "fh1",
        text: "How would you honestly describe your energy throughout the day?",
        type: "choice",
        options: [
          "Crashing by noon — I'm running on caffeine and survival mode",
          "Inconsistent — good days and completely wiped days with no pattern",
          "Decent, but I hit a wall and rely on stimulants to push through",
          "Steady and sustainable — no crashes, no desperate coffee runs",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "fh2",
        text: "How well are you sleeping?",
        type: "choice",
        options: [
          "I fall asleep fine but wake up exhausted — something isn't right",
          "I struggle to fall or stay asleep consistently",
          "I sleep, but I don't wake up feeling restored",
          "I sleep deeply and wake up genuinely rested",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "fh3",
        text: "Have you ever had your hormones, thyroid, or metabolic markers assessed?",
        type: "choice",
        options: [
          "Never — I didn't realize that was something I should track",
          "I've had basic bloodwork but never looked at it closely",
          "Once or twice, but no one really explained what the results meant",
          "Yes — I monitor my biomarkers and understand what they mean",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "fh4",
        text: "Which of these are you currently experiencing? (Select all that apply)",
        type: "multi",
        options: [
          "Brain fog or inability to focus for long stretches",
          "Strong sugar or carb cravings — especially in the afternoon",
          "Hair thinning or excessive shedding",
          "Low sex drive or hormonal irregularities",
          "Difficulty losing body fat despite consistent effort",
          "Bloating or digestive discomfort after eating",
          "Mood swings, irritability, or emotional flatness",
          "Feeling cold often, especially hands and feet",
          "None of these — I'm functioning well",
        ],
      },
      {
        id: "fh5",
        text: "How would you describe your body's response to stress?",
        type: "choice",
        options: [
          "I stay in a heightened state — I can't wind down even when I want to",
          "I push through stress and pay for it physically afterward",
          "Stress affects me but I recover reasonably well",
          "I have solid tools and my nervous system handles stress adaptively",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "fh6",
        text: "How long does it take you to recover after a hard training session or demanding week?",
        type: "choice",
        options: [
          "Days — I'm always carrying soreness and fatigue into my next session",
          "24–48 hours but it significantly impacts my performance",
          "I recover okay but I know I could be faster",
          "I recover well and feel ready to go again quickly",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "fh7",
        text: "How connected are you to your hunger and fullness cues?",
        type: "choice",
        options: [
          "I eat emotionally or impulsively — hunger doesn't drive my intake",
          "I follow a schedule but can't actually feel whether I'm hungry",
          "I notice hunger but often ignore or override it",
          "I'm deeply attuned — I eat when hungry and stop when satisfied",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "fh8",
        text: "How much of your physical discomfort do you accept as 'just normal'?",
        type: "choice",
        options: [
          "A lot — I've normalized constant fatigue, bloating, and aches",
          "Some — I know things feel off but I don't know how to address it",
          "Occasionally — I have rough patches but generally feel okay",
          "Rarely — I take symptoms seriously and address them proactively",
        ],
        weights: [4, 3, 2, 1],
      },
    ],
  },
  {
    id: "mental",
    label: "Mental Habits & Identity",
    tag: "PART 02",
    accent: "#7DF9FF",
    intro: "Your transformation is more than just physical. This is where most people's real work lives.",
    questions: [
      {
        id: "mh1",
        text: "When you commit to a health goal, what most often happens?",
        type: "choice",
        options: [
          "I start with intensity and flame out within 2–3 weeks",
          "I make real progress, then something triggers a full restart",
          "I stay consistent until life gets hard — then I disappear",
          "I follow through. Setbacks are temporary and don't define my trajectory",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "mh2",
        text: "When you miss a workout or eat off-plan, how do you respond?",
        type: "choice",
        options: [
          "I spiral — one slip becomes 'I'll restart Monday'",
          "I beat myself up hard, but eventually get back on track",
          "I feel guilty but shake it off fairly quickly",
          "I treat it as neutral data and get back on track immediately",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "mh3",
        text: "Which mental patterns do you recognize in yourself? (Select all that apply)",
        type: "multi",
        options: [
          "All-or-nothing thinking ('I already ruined it, might as well...')",
          "Tying your self-worth to the number on the scale or your body",
          "Comparing your progress to others in a way that deflates you",
          "Waiting for the 'perfect time' to start or recommit",
          "Perfectionism that leads to procrastination or paralysis",
          "Using food as your primary stress relief or emotional outlet",
          "Imposter syndrome — you talk about transformation but don't feel it",
          "Shrinking yourself to avoid judgment from people around you",
          "None of these — I have a strong, stable relationship with myself",
        ],
      },
      {
        id: "mh4",
        text: "How clear is your 'why' for wanting to transform your body and life?",
        type: "choice",
        options: [
          "Unclear — I just know I should be doing more",
          "I have a surface-level reason but it doesn't actually drive me",
          "I know why, but I lose sight of it when things get hard",
          "My why is rooted, specific, and moves me even on the hardest days",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "mh5",
        text: "When a coach challenges your beliefs about food, training, or your body — how do you respond?",
        type: "choice",
        options: [
          "I get defensive and struggle to let go of what I already believe",
          "I'm skeptical and need a lot of evidence before I trust new info",
          "I'm open but it takes real time before I genuinely act on it",
          "I'm coachable and curious — I love being given a better framework",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "mh6",
        text: "How much does your environment support your goals? (people, home setup, schedule)",
        type: "choice",
        options: [
          "It actively works against me — I'm swimming upstream every day",
          "It's neutral at best — no one's stopping me but no one's helping",
          "It's mostly supportive but there are real friction points",
          "My environment is deliberately built to support my success",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "mh7",
        text: "Have you ever invested in coaching, a program, or a course and not followed through?",
        type: "choice",
        options: [
          "Yes — more than once. I recognize it as a pattern I haven't broken",
          "Once or twice — life circumstances genuinely derailed me",
          "I've always followed through once I commit and invest",
          "I've never invested in coaching before",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "mh8",
        text: "Finish this honestly: The version of me I want to become feels...",
        type: "choice",
        options: [
          "Like a fantasy — I don't actually believe I can get there",
          "Possible for others, but somehow just out of reach for me",
          "Achievable, but I don't have the roadmap to get there yet",
          "Inevitable — I just need the right structure and accountability",
        ],
        weights: [4, 3, 2, 1],
      },
    ],
  },
  {
    id: "training",
    label: "Training & Body",
    tag: "PART 03",
    accent: "#FFB347",
    intro: "What's actually happening inside your training — and what's not.",
    questions: [
      {
        id: "tr1",
        text: "How would you describe your training consistency over the last 12 weeks?",
        type: "choice",
        options: [
          "Non-existent — I've barely touched the gym",
          "Sporadic — I go in waves, usually sparked by guilt or a burst of motivation",
          "Moderate — 1–2x per week but without real intention behind it",
          "Consistent and structured — 3–5x per week with clear purpose",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "tr2",
        text: "Do you train with a structured, progressive program?",
        type: "choice",
        options: [
          "No — I just do whatever I feel like that day",
          "I have some structure but no real progression built in",
          "I have a program but don't consistently track or adjust it",
          "Yes — I follow a plan, measure output, and progress intentionally",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "tr3",
        text: "How would you describe your results relative to the effort you put in?",
        type: "choice",
        options: [
          "I work hard and see almost nothing — it's genuinely demoralizing",
          "Slow progress that doesn't match the sacrifice I'm making",
          "Decent progress, but I know I'm leaving results on the table",
          "My results consistently reflect the work I put in",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "tr4",
        text: "Do you currently track your nutrition in any intentional way?",
        type: "choice",
        options: [
          "Not at all — I eat without any framework",
          "I try to eat 'healthy' but nothing is measured or structured",
          "I track loosely or inconsistently",
          "I consistently track and use data to make real adjustments",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "tr5",
        text: "How well do you understand your own body — how it responds to different training styles, foods, and recovery inputs?",
        type: "choice",
        options: [
          "Barely at all — I'm guessing most of the time",
          "I have some intuition but no real framework or data behind it",
          "I'm learning, but there are significant blind spots",
          "I have deep self-knowledge backed by real feedback and results",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "tr6",
        text: "Which of these describe your training history? (Select all that apply)",
        type: "multi",
        options: [
          "Tried multiple diets (keto, low-carb, IF, etc.) with short-lived results",
          "Mostly cardio-focused training without structured resistance work",
          "Group fitness classes as my primary or only training method",
          "Started and stopped multiple programs without finishing them",
          "Worked with a personal trainer but not gotten sustainable results",
          "Used apps or online programs without real accountability",
          "Trained seriously at some point and fallen significantly off since",
          "Fairly new — I don't have much training history yet",
        ],
      },
      {
        id: "tr7",
        text: "Do you understand how to build muscle — specifically how progressive overload works and how to apply it?",
        type: "choice",
        options: [
          "I don't really know what progressive overload means",
          "I've heard the term but I'm not confidently applying it",
          "I understand the concept but my execution is inconsistent",
          "Yes — I understand and consistently apply it to drive adaptation",
        ],
        weights: [4, 3, 2, 1],
      },
      {
        id: "tr8",
        text: "What is your biggest frustration with where you are physically right now?",
        type: "choice",
        options: [
          "I don't know where to start — I feel completely lost",
          "I plateau and stall no matter what I try or change",
          "I push hard, burn out or get injured, and restart from zero",
          "I know what to do — I just can't stay consistent long enough to see it work",
        ],
        weights: [4, 3, 2, 1],
      },
    ],
  },
];

/* ─────────────────────────────────────────────
   PROFILE ENGINE
───────────────────────────────────────────── */
function computeProfile(answers) {
  let totals = { functional: 0, mental: 0, training: 0 };
  let counts = { functional: 0, mental: 0, training: 0 };
  sections.forEach((sec) => {
    sec.questions.forEach((q) => {
      const a = answers[q.id];
      if (!a) return;
      let score = 2;
      if (q.type === "choice" && q.weights) {
        const idx = q.options.indexOf(a);
        if (idx >= 0) score = q.weights[idx];
      } else if (q.type === "multi" && Array.isArray(a)) {
        score = Math.min(a.filter((x) => !x.startsWith("None")).length + 1, 4);
      }
      totals[sec.id] += score;
      counts[sec.id]++;
    });
  });
  const avgs = {
    functional: counts.functional ? totals.functional / counts.functional : 2,
    mental:     counts.mental     ? totals.mental     / counts.mental     : 2,
    training:   counts.training   ? totals.training   / counts.training   : 2,
  };
  const overall = (avgs.functional + avgs.mental + avgs.training) / 3;
  const scores = {
    functional: Math.round(((4 - avgs.functional) / 3) * 100),
    mental:     Math.round(((4 - avgs.mental)     / 3) * 100),
    training:   Math.round(((4 - avgs.training)   / 3) * 100),
  };
  let profile;
  if (overall >= 3.1) {
    profile = {
      title: "The Disconnected High-Achiever",
      headline: "You work hard. But you're working blind.",
      summary: "You have real desire and even some drive — but you're missing the individualized structure to match your effort to lasting results. Something is off in how your body is functioning, how you're thinking about yourself, and how your training is built — and none of it is your fault. You've been working with generic tools on a very specific problem.",
      callout: "Your transformation has been waiting. You've just been solving the wrong problem.",
    };
  } else if (overall >= 2.4) {
    profile = {
      title: "The Plateau Dweller",
      headline: "You're doing 'everything right' — and still stuck.",
      summary: "You've built some real habits, you're not starting from zero, and you're not lazy. But you've hit a ceiling. Progress has stalled, motivation wavers, and the strategies you've tried don't apply to your specific body or your life. You don't need more willpower or a stricter plan. You need someone who treats you as an individual.",
      callout: "The gap isn't effort. It's personalization — and that's exactly what's missing.",
    };
  } else {
    profile = {
      title: "The Inconsistent Starter",
      headline: "You have everything you need. Except the right container.",
      summary: "You understand more than you give yourself credit for, and you've had real moments of momentum. But something keeps breaking the chain — a mental block, a plan that doesn't fit your life, or the absence of someone holding the standard when you can't. The missing piece isn't a better plan. It's having someone build it with you.",
      callout: "Consistency isn't a character flaw. It's a system problem. Let's fix the system.",
    };
  }
  return { profile, scores };
}

/* ─────────────────────────────────────────────
   SHARED STYLES
───────────────────────────────────────────── */
const mono = (color = "#C8F562") => ({
  fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase",
  color, fontFamily: "'Courier New', monospace",
});
const gridBg = {
  position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
  backgroundImage: "linear-gradient(rgba(255,255,255,0.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.018) 1px,transparent 1px)",
  backgroundSize: "56px 56px",
};
const btnGreen = {
  background: "#C8F562", color: "#080808", border: "none",
  padding: "17px 52px", fontSize: 12, letterSpacing: "0.22em",
  textTransform: "uppercase", fontFamily: "'Courier New', monospace",
  fontWeight: 700, cursor: "pointer",
};

/* ─────────────────────────────────────────────
   SCORE BAR
───────────────────────────────────────────── */
function ScoreBar({ label, value, color, delay }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  const level = value >= 70 ? "Strong" : value >= 45 ? "Developing" : "Needs Work";
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ ...mono("#555") }}>{label}</span>
        <span style={{ fontSize: 10, color, fontFamily: "'Courier New', monospace", letterSpacing: "0.1em" }}>{level}</span>
      </div>
      <div style={{ background: "#161616", borderRadius: 2, height: 3, overflow: "hidden" }}>
        <div style={{ width: `${w}%`, height: "100%", background: color, transition: "width 1.4s cubic-bezier(.16,1,.3,1)", borderRadius: 2 }} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN APP
───────────────────────────────────────────── */
export default function App() {
  const [phase, setPhase]           = useState("intro");
  const [intake, setIntake]         = useState({ firstName: "", lastName: "", instagram: "" });
  const [intakeErrors, setIntakeErrors] = useState({});
  const [sectionIdx, setSectionIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [answers, setAnswers]       = useState({});
  const [selected, setSelected]     = useState(null);
  const [multiSel, setMultiSel]     = useState([]);
  const [fading, setFading]         = useState(false);
  const [sectionBridge, setSectionBridge] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const currentSection = sections[sectionIdx];
  const currentQ       = currentSection?.questions[questionIdx];
  const totalQ         = sections.reduce((a, s) => a + s.questions.length, 0);
  const answeredQ      = Object.keys(answers).length;
  const globalPct      = Math.round((answeredQ / totalQ) * 100);
  const canAdvance     = currentQ?.type === "multi" ? multiSel.length > 0 : selected !== null;

  const result = phase === "results" ? computeProfile(answers) : null;

  // Scroll to top on phase/question change
  useEffect(() => { window.scrollTo(0, 0); }, [phase, sectionIdx, questionIdx]);

  /* ── INTAKE ── */
  const submitIntake = () => {
    const errs = {};
    if (!intake.firstName.trim()) errs.firstName = true;
    if (!intake.lastName.trim())  errs.lastName  = true;
    if (!intake.instagram.trim()) errs.instagram = true;
    setIntakeErrors(errs);
    if (!Object.keys(errs).length) setPhase("quiz");
  };

  /* ── ADVANCE QUESTION ── */
  const advance = async () => {
    if (fading || !canAdvance) return;
    const ans = currentQ.type === "multi" ? [...multiSel] : selected;
    const newAnswers = { ...answers, [currentQ.id]: ans };
    setAnswers(newAnswers);
    setFading(true);
    await new Promise((r) => setTimeout(r, 220));
    setSelected(null);
    setMultiSel([]);

    const nextQ = questionIdx + 1;
    if (nextQ < currentSection.questions.length) {
      setQuestionIdx(nextQ);
      setFading(false);
    } else if (sectionIdx + 1 < sections.length) {
      setSectionBridge(true);
      await new Promise((r) => setTimeout(r, 1400));
      setSectionIdx(sectionIdx + 1);
      setQuestionIdx(0);
      setSectionBridge(false);
      setFading(false);
    } else {
      // Final question — build payload and submit to Formspree
      const { profile, scores } = computeProfile(newAnswers);
      const ig = intake.instagram.trim().startsWith("@")
        ? intake.instagram.trim()
        : `@${intake.instagram.trim()}`;
      const timestamp = new Date().toLocaleString("en-US", { timeZone: "America/New_York" });

      const answerSummary = sections.map((sec) =>
        `\n=== ${sec.label.toUpperCase()} ===\n` +
        sec.questions.map((q) => {
          const a = newAnswers[q.id];
          return `Q: ${q.text}\nA: ${Array.isArray(a) ? a.join(" | ") : (a ?? "—")}`;
        }).join("\n\n")
      ).join("\n");

      setSubmitting(true);
      setPhase("submitting");

      // Direct fetch to Formspree — most reliable approach
      try {
        await fetch("https://formspree.io/f/xzdygvkg", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            _subject:           `New Assessment: ${intake.firstName.trim()} ${intake.lastName.trim()} (${ig})`,
            "First Name":       intake.firstName.trim(),
            "Last Name":        intake.lastName.trim(),
            "Full Name":        `${intake.firstName.trim()} ${intake.lastName.trim()}`,
            Instagram:          ig,
            Profile:            profile.title,
            "Profile Headline": profile.headline,
            "Score - Functional Health":   `${scores.functional}%`,
            "Score - Mental Habits":       `${scores.mental}%`,
            "Score - Training and Body":   `${scores.training}%`,
            "Submitted At":     timestamp,
            "All Answers":      answerSummary,
          }),
        });
      } catch (e) {
        // Silent fail — always show results regardless
      }

      setSubmitting(false);
      setPhase("results");
    }
  };

  const toggleMulti = (opt) =>
    setMultiSel((p) => p.includes(opt) ? p.filter((o) => o !== opt) : [...p, opt]);

  const shell = {
    minHeight: "100vh", background: "#080808", color: "#F0EDE8",
    fontFamily: "Georgia, 'Times New Roman', serif",
  };
  const inputBase = (err) => ({
    background: "#0D0D0D", border: `1px solid ${err ? "#FF6B6B" : "#242424"}`,
    color: "#F0EDE8", padding: "14px 18px", fontSize: 15,
    fontFamily: "Georgia, serif", outline: "none",
    width: "100%", boxSizing: "border-box", transition: "border-color 0.2s",
  });

  /* ══════════════════ INTRO ══════════════════ */
  if (phase === "intro") return (
    <div style={shell}>
      <div style={gridBg} />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 620, margin: "0 auto", padding: "72px 24px 80px", display: "flex", flexDirection: "column", minHeight: "100vh", justifyContent: "center" }}>
        <div style={{ ...mono(), marginBottom: 40 }}>@trainedbyannie · Beyond Built Training</div>
        <div style={{ ...mono("#444"), marginBottom: 14 }}>THE FULL ASSESSMENT</div>
        <h1 style={{ fontSize: "clamp(34px, 7vw, 70px)", lineHeight: 1.06, fontWeight: 400, margin: "0 0 26px", letterSpacing: "-0.03em" }}>
          Your transformation<br />
          <em style={{ fontStyle: "italic", color: "#C8F562" }}>is more than physical.</em>
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.78, color: "#888", maxWidth: 460, margin: "0 0 48px" }}>
          Most people are working with incomplete information — about their body, their mind, and their training. This assessment reveals exactly where your gaps are and whether coaching with Annie is your right next step.
        </p>
        <div style={{ display: "flex", gap: 28, marginBottom: 48, flexWrap: "wrap" }}>
          {[["Functional Health","#C8F562"],["Mental Habits","#7DF9FF"],["Training & Body","#FFB347"]].map(([l,c]) => (
            <div key={l} style={{ display: "flex", alignItems: "center", gap: 8, ...mono("#555") }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: c }} />{l}
            </div>
          ))}
        </div>
        <div>
          <button style={btnGreen} onClick={() => setPhase("intake")}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}>
            Begin Assessment →
          </button>
          <p style={{ ...mono("#333"), marginTop: 18 }}>~8 minutes · 24 questions · No fluff</p>
        </div>
      </div>
    </div>
  );

  /* ══════════════════ INTAKE ══════════════════ */
  if (phase === "intake") return (
    <div style={shell}>
      <div style={gridBg} />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 540, margin: "0 auto", padding: "72px 24px 80px", display: "flex", flexDirection: "column", minHeight: "100vh", justifyContent: "center" }}>
        <div style={{ ...mono(), marginBottom: 36 }}>BEFORE WE BEGIN</div>
        <h2 style={{ fontSize: "clamp(26px, 5vw, 44px)", lineHeight: 1.15, fontWeight: 400, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
          Tell us a little about yourself.
        </h2>
        <p style={{ fontSize: 14, color: "#666", margin: "0 0 44px", lineHeight: 1.7 }}>
          Your responses will be sent directly to Annie so she can review your results personally.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 36 }}>
          {[
            { key: "firstName", label: "First Name",       placeholder: "First"       },
            { key: "lastName",  label: "Last Name",        placeholder: "Last"        },
            { key: "instagram", label: "Instagram Handle", placeholder: "@yourhandle" },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label style={{ ...mono("#555"), display: "block", marginBottom: 9 }}>{label}</label>
              <input
                style={inputBase(intakeErrors[key])}
                placeholder={placeholder}
                value={intake[key]}
                onChange={(e) => setIntake((p) => ({ ...p, [key]: e.target.value }))}
                onFocus={(e)   => { e.target.style.borderColor = "#C8F562"; }}
                onBlur={(e)    => { e.target.style.borderColor = intakeErrors[key] ? "#FF6B6B" : "#242424"; }}
                onKeyDown={(e) => { if (e.key === "Enter") submitIntake(); }}
              />
              {intakeErrors[key] && (
                <p style={{ color: "#FF6B6B", fontSize: 11, marginTop: 6, fontFamily: "'Courier New', monospace" }}>Required</p>
              )}
            </div>
          ))}
        </div>
        <button style={btnGreen} onClick={submitIntake}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}>
          Start Assessment →
        </button>
      </div>
    </div>
  );

  /* ══════════════════ SUBMITTING ══════════════════ */
  if (phase === "submitting") return (
    <div style={{ ...shell, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={gridBg} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
        <div style={{ width: 36, height: 36, border: "2px solid #1E1E1E", borderTopColor: "#C8F562", borderRadius: "50%", animation: "spin 0.75s linear infinite", margin: "0 auto 24px" }} />
        <p style={{ ...mono("#444") }}>Sending your results to Annie…</p>
      </div>
    </div>
  );

  /* ══════════════════ RESULTS ══════════════════ */
  if (phase === "results") {
    const { profile, scores } = result;
    return (
      <div style={shell}>
        <div style={gridBg} />
        <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        <div style={{ position: "relative", zIndex: 1, maxWidth: 660, margin: "0 auto", padding: "64px 24px 100px", animation: "fadeIn 0.6s ease both" }}>

          <div style={{ ...mono(), marginBottom: 32 }}>ASSESSMENT COMPLETE · @trainedbyannie</div>
          <div style={{ ...mono("#444"), marginBottom: 12 }}>YOUR PROFILE, {intake.firstName.toUpperCase()}</div>
          <h2 style={{ fontSize: "clamp(28px, 5vw, 54px)", lineHeight: 1.08, fontWeight: 400, margin: "0 0 14px", letterSpacing: "-0.025em" }}>
            {profile.title}
          </h2>
          <p style={{ fontSize: "clamp(17px, 2.8vw, 25px)", color: "#C8F562", fontStyle: "italic", margin: "0 0 26px" }}>
            {profile.headline}
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: "#999", maxWidth: 520, margin: "0 0 52px" }}>
            {profile.summary}
          </p>

          <div style={{ background: "#0D0D0D", border: "1px solid #1A1A1A", padding: "34px 32px", marginBottom: 40 }}>
            <div style={{ ...mono("#444"), marginBottom: 28 }}>WHERE YOU STAND</div>
            <ScoreBar label="Functional Health"        value={scores.functional} color="#C8F562" delay={300} />
            <ScoreBar label="Mental Habits & Identity"  value={scores.mental}     color="#7DF9FF" delay={600} />
            <ScoreBar label="Training & Body"           value={scores.training}   color="#FFB347" delay={900} />
          </div>

          <div style={{ borderLeft: "3px solid #C8F562", paddingLeft: 26, marginBottom: 52 }}>
            <p style={{ fontSize: 18, lineHeight: 1.65, fontStyle: "italic", color: "#CCC", margin: "0 0 16px" }}>
              "{profile.callout}"
            </p>
            <p style={{ ...mono("#444") }}>— Annie · @trainedbyannie · Beyond Built Training</p>
          </div>

          <div style={{ marginBottom: 56 }}>
            <div style={{ ...mono("#444"), marginBottom: 26 }}>WHAT THIS MEANS FOR YOU</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { color: "#C8F562", head: "Functional Health Assessment", body: "Your body communicates through symptoms you've likely normalized. Working with Annie means understanding your metabolic markers, hormone health, recovery capacity, and how your physiology specifically needs to be supported — not guessed at." },
                { color: "#7DF9FF", head: "Mental Habit Rewiring",        body: "At Beyond Built, transformation is #morethanjustphysical. Your mental patterns, your identity, your relationship with your body — these are coachable. Annie works on the beliefs and behaviors underneath the physical results, because that's where lasting change actually lives." },
                { color: "#FFB347", head: "Individualized Training & Nutrition", body: "No templates. No cookie-cutter macros. Every protocol Annie builds is designed around your physique, your lifestyle, your recovery, and your goals — with data-driven adjustments as you progress." },
              ].map((c, i) => (
                <div key={i} style={{ background: "#0C0C0C", border: "1px solid #181818", padding: "22px 26px", display: "flex", gap: 18 }}>
                  <div style={{ width: 3, minHeight: 50, background: c.color, flexShrink: 0, borderRadius: 2 }} />
                  <div>
                    <p style={{ ...mono(c.color), margin: "0 0 10px" }}>{c.head}</p>
                    <p style={{ fontSize: 13, lineHeight: 1.72, color: "#777", margin: 0 }}>{c.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "#0C0C0C", border: "1px solid #252525", padding: "48px 36px", textAlign: "center" }}>
            <div style={{ ...mono(), marginBottom: 20 }}>NEXT STEP</div>
            <h3 style={{ fontSize: "clamp(22px, 4vw, 38px)", lineHeight: 1.15, fontWeight: 400, margin: "0 0 16px", letterSpacing: "-0.02em" }}>
              Ready to stop guessing<br />
              <em style={{ fontStyle: "italic", color: "#C8F562" }}>and start building?</em>
            </h3>
            <p style={{ fontSize: 14, lineHeight: 1.75, color: "#666", maxWidth: 400, margin: "0 auto 36px" }}>
              Annie has your results. Book a free discovery call — we'll review where you are, where you want to go, and whether coaching is the right fit.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
              <a href="https://www.instagram.com/trainedbyannie/" target="_blank" rel="noopener noreferrer"
                style={{ ...btnGreen, display: "inline-block", textDecoration: "none" }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}>
                DM Annie on Instagram →
              </a>
              <a href="https://www.beyondbuilttraining.com" target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", border: "1px solid #2A2A2A", color: "#555", padding: "13px 36px", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontFamily: "'Courier New', monospace", textDecoration: "none" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#C8F562"; e.currentTarget.style.color = "#C8F562"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#2A2A2A"; e.currentTarget.style.color = "#555"; }}>
                Apply at BeyondBuiltTraining.com
              </a>
            </div>
            <p style={{ ...mono("#2E2E2E"), marginTop: 28 }}>#morethanjustphysical</p>
          </div>
        </div>
      </div>
    );
  }

  /* ══════════════════ QUIZ ══════════════════ */
  const accent = currentSection.accent;
  return (
    <div style={shell}>
      <div style={gridBg} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {sectionBridge && (
        <div style={{ position: "fixed", inset: 0, zIndex: 30, background: "#080808", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }}>
          <div style={{ ...mono(accent), marginBottom: 4 }}>SECTION COMPLETE</div>
          <p style={{ fontSize: "clamp(20px, 4vw, 32px)", fontWeight: 400, color: "#DDD", margin: 0 }}>{sections[sectionIdx + 1]?.label}</p>
          <p style={{ fontSize: 13, color: "#555", fontStyle: "italic", margin: 0 }}>{sections[sectionIdx + 1]?.intro}</p>
        </div>
      )}

      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 20, background: "#080808ee", backdropFilter: "blur(10px)", borderBottom: "1px solid #111" }}>
        <div style={{ maxWidth: 660, margin: "0 auto", padding: "13px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ ...mono("#2A2A2A") }}>@trainedbyannie</span>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 110, height: 2, background: "#161616", borderRadius: 1 }}>
              <div style={{ width: `${globalPct}%`, height: "100%", background: accent, transition: "width 0.5s ease", borderRadius: 1 }} />
            </div>
            <span style={{ ...mono("#333"), fontSize: 9 }}>{globalPct}%</span>
          </div>
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 1, maxWidth: 640, margin: "0 auto", padding: "96px 24px 120px", opacity: fading ? 0 : 1, transition: "opacity 0.22s ease" }}>
        <div style={{ marginBottom: 38 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ ...mono(accent) }}>{currentSection.tag} — {currentSection.label}</span>
            <span style={{ ...mono("#333"), fontSize: 9 }}>{questionIdx + 1} / {currentSection.questions.length}</span>
          </div>
          <div style={{ height: 2, background: "#161616", borderRadius: 1 }}>
            <div style={{ width: `${(questionIdx / currentSection.questions.length) * 100}%`, height: "100%", background: accent, transition: "width 0.4s ease", borderRadius: 1 }} />
          </div>
        </div>

        <h2 style={{ fontSize: "clamp(20px, 3.5vw, 30px)", lineHeight: 1.32, fontWeight: 400, margin: "0 0 36px", letterSpacing: "-0.01em" }}>
          {currentQ.text}
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {currentQ.options.map((opt, i) => {
            const active = currentQ.type === "multi" ? multiSel.includes(opt) : selected === opt;
            return (
              <button key={i}
                onClick={() => currentQ.type === "multi" ? toggleMulti(opt) : setSelected(opt)}
                style={{
                  background: active ? "#111" : "transparent",
                  border: `1px solid ${active ? accent : "#1E1E1E"}`,
                  color: active ? "#F0EDE8" : "#555",
                  padding: "15px 20px", textAlign: "left", fontSize: 14,
                  lineHeight: 1.52, cursor: "pointer",
                  display: "flex", alignItems: "flex-start", gap: 14,
                  transition: "border-color 0.14s, color 0.14s, background 0.14s",
                  fontFamily: "Georgia, serif",
                }}
                onMouseEnter={(e) => { if (!active) { e.currentTarget.style.borderColor = "#333"; e.currentTarget.style.color = "#999"; } }}
                onMouseLeave={(e) => { if (!active) { e.currentTarget.style.borderColor = "#1E1E1E"; e.currentTarget.style.color = "#555"; } }}
              >
                <div style={{
                  width: 16, height: 16, flexShrink: 0, marginTop: 2,
                  border: `1px solid ${active ? accent : "#2E2E2E"}`,
                  borderRadius: currentQ.type === "multi" ? 3 : "50%",
                  background: active ? accent : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.14s, border-color 0.14s",
                }}>
                  {active && <div style={{ width: 6, height: 6, borderRadius: currentQ.type === "multi" ? 1 : "50%", background: "#080808" }} />}
                </div>
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 20, background: "#080808ee", backdropFilter: "blur(10px)", borderTop: "1px solid #111", padding: "14px 24px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ ...mono("#222"), fontSize: 9 }}>
            {intake.firstName} {intake.lastName} · {intake.instagram.startsWith("@") ? intake.instagram : `@${intake.instagram}`}
          </span>
          <button onClick={advance} disabled={!canAdvance}
            style={{
              ...btnGreen,
              background: canAdvance ? accent : "#111",
              color: canAdvance ? "#080808" : "#2E2E2E",
              cursor: canAdvance ? "pointer" : "not-allowed",
              padding: "13px 36px",
            }}
            onMouseEnter={(e) => { if (canAdvance) e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}>
            {questionIdx + 1 === currentSection.questions.length && sectionIdx + 1 === sections.length
              ? "See My Results →" : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}
